import { useContext, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import AppBackService from "../services/appback";
import LiveSystemSelect from "./LiveSystemSelect";
import { readLiveSystem, writeLiveSystem } from "../liveTap/liveSystem";
import { RootContext } from "../context/root-provider";
import useSelectLayout from "../hooks/useSelectLayout";
import { liveArriving, spokenOf } from "../services/spokenDocs";

const styles = {
  dialog: {
    height: "100%",
    "& .MuiPaper-elevation": { height: "100%" },
  },
};

const CreateTapModal = ({ open, setOpen, doc, refreshCallback, showSnackbar }) => {
  const { userInfo, notifInterval } = useContext(RootContext);
  const navigate = useNavigate();

  const { error, selectedLayout, selectedBackground, layoutSelector, resetSelection } = useSelectLayout();

  // A still-running doc opens the LIVE tapestry, where layout/background
  // selection doesn't apply — the step that does is which rendering system
  // the Live Tap page should use (TT-1046; classic default, persisted
  // client-side per doc). Still running is the doc model's word: its source
  // arriving live (`doc.spoken`, from the doc read, TT-1273). A row whose
  // `spoken` has not come (the list's many-doc read is async, and may fail)
  // is read here on open, and Create holds until it is known: guessing
  // "not live" would make a deriv tap of a running meeting.
  const [readSpoken, setReadSpoken] = useState(null); // { docId, spoken } | { docId, failed }
  const given = doc?.spoken || null;
  const fetched = readSpoken?.docId === doc?.docId ? readSpoken : null;
  const spoken = given || fetched?.spoken || null;
  const known = Boolean(spoken);
  const isLive = liveArriving(spoken);
  const [liveSystem, setLiveSystem] = useState(() => readLiveSystem(doc?.docId));

  // Re-seed the selection each open from the doc's persisted choice, so a doc
  // previously set to scene shows scene and one doc's pick doesn't leak into
  // another through this shared modal instance.
  useEffect(() => {
    if (open) setLiveSystem(readLiveSystem(doc?.docId));
  }, [open, doc?.docId]);

  const docId = doc?.docId;
  const readOnOpen = open && !given && Boolean(docId) && !fetched?.spoken;
  const readDoc = useCallback(async () => {
    setReadSpoken({ docId, reading: true });
    const read = spokenOf(await AppBackService.getSpokenDoc(userInfo?.username, docId));
    const result = read ? { docId, spoken: read } : { docId, failed: true };
    setReadSpoken((prev) => (prev?.docId === docId ? result : prev));
  }, [docId, userInfo]);

  useEffect(() => {
    if (readOnOpen && !fetched) readDoc();
  }, [readOnOpen, fetched, readDoc]);

  // A read that failed is tried again on the next open.
  useEffect(() => {
    if (!open) setReadSpoken((prev) => (prev?.spoken ? prev : null));
  }, [open]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const doCreateTap = useCallback(
    async (layoutId, backgroundId) => {
      if (doc && known) {
        // Mid-meeting batch tapestry: a still-running live doc renders through
        // the live-tap renderer, its live cassette read at the frontier it has
        // reached, never the delete-on-drip deriv plane. Only closed docs
        // materialize a deriv tap.
        if (isLive) {
          writeLiveSystem(doc.docId, liveSystem); // TT-1046 — per-doc system choice
          setOpen(false);
          navigate(`/live-tap/${doc.docId}?checkpoint=1`);
          return;
        }
        const resp = await AppBackService.createUserTap(userInfo?.username, {
          doc_id: doc.docId,
          layout_id: layoutId,
          background_id: backgroundId,
        });

        if (resp && !resp.error) {
          showSnackbar("Creating Tapestry...", "info");
        } else {
          showSnackbar("Error creating Tapestry", "error");
        }
        // start notification polling
        // 10 seconds * 6 intervals = 1 minute for tapestry to be created
        notifInterval(refreshCallback, showSnackbar, 10000, 6, false);
        setOpen(false);
      }
    },
    [doc, known, isLive, liveSystem, navigate, notifInterval, refreshCallback, setOpen, showSnackbar, userInfo]
  );

  const createTap = useCallback(
    () => doCreateTap(selectedLayout, selectedBackground),
    [doCreateTap, selectedLayout, selectedBackground]
  );

  const autoCreate = useCallback(() => {
    resetSelection();
    doCreateTap(null, null);
  }, [doCreateTap, resetSelection]);

  // handle enter key
  useEffect(() => {
    function handleKeyPress(event) {
      // Enter inside the system radiogroup selects a card (LiveSystemSelect
      // stops propagation too); it must never double as "create" — that would
      // run with the pre-selection liveSystem closure.
      if (event.target instanceof Element && event.target.closest('[role="radiogroup"]')) {
        return;
      }
      if (event.key === "Enter" && open && known && !error) {
        doCreateTap(selectedLayout, selectedBackground);
      }
    }

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [doCreateTap, error, known, open, selectedLayout, selectedBackground]);

  return (
    <Dialog sx={isLive || !known ? undefined : styles.dialog} fullWidth maxWidth={isLive || !known ? "sm" : "xl"} open={open} onClose={closeModal}>
      <DialogTitle>
        {isLive ? "Live Tapestry" : "Create a Tapestry"} - <b>{doc?.title}</b>
      </DialogTitle>
      <DialogContent>
        {!known ? (
          <DialogContentText>
            {fetched?.failed ? (
              <>
                Could not tell whether this meeting is still live. <Button onClick={readDoc}>Try again</Button>
              </>
            ) : (
              "Checking whether this meeting is still live…"
            )}
          </DialogContentText>
        ) : isLive ? (
          <>
            <DialogContentText sx={{ marginBottom: "8px" }}>
              This meeting is still live — choose how its Tapestry is drawn.
            </DialogContentText>
            <LiveSystemSelect value={liveSystem} onChange={setLiveSystem} />
          </>
        ) : (
          layoutSelector
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Cancel</Button>
        {!known ? (
          <Button disabled>Create</Button>
        ) : isLive ? (
          <Button onClick={createTap}>Open Live Tapestry</Button>
        ) : (
          <>
            <Button disabled={error} onClick={autoCreate} variant={selectedBackground ? undefined : "outlined"}>
              Auto Create
            </Button>
            <Button disabled={error} onClick={createTap} variant={!selectedBackground ? undefined : "outlined"}>
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
CreateTapModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  doc: PropTypes.object,
  refreshCallback: PropTypes.func,
  showSnackbar: PropTypes.func,
};

export default CreateTapModal;
